<template>
  <v-card class="mx-8 mt-8">
    <v-stepper
      v-model="activeStep"
      class="custom-header"
    >
      <!-- Header -->
      <v-stepper-header>
        <!-- Header: Step 1 -->
        <v-stepper-step
          :complete="activeStep > 1"
          step="1"
          :rules="[() => stepValidationResult[1]]"
        >
          <div class="d-flex align-center">
            <span
              class="text-4xl font-weight-bold me-3"
              :class="stepValidationResult[1] ? 'text--primary' : 'error--text'"
            >01</span>
            <div class="d-flex flex-column">
              <span
                class="text-sm font-weight-semibold"
                :class="stepValidationResult[1] ? 'text--primary' : 'error--text'"
              >CONTA</span>
              <span
                class="text-xs"
                :class="stepValidationResult[1] ? 'text--secondary' : 'error--text'"
              >Configure os detalhes da sua conta</span>
            </div>
          </div>
        </v-stepper-step>

        <v-divider></v-divider>

        <!-- Header: Step 2 -->
        <v-stepper-step
          :complete="activeStep > 2"
          step="2"
          :rules="[() => stepValidationResult[2]]"
        >
          <div class="d-flex align-center">
            <span
              class="text-4xl font-weight-bold me-3"
              :class="stepValidationResult[2] ? 'text--primary' : 'error--text'"
            >02</span>
            <div class="d-flex flex-column">
              <span
                class="text-sm font-weight-semibold"
                :class="stepValidationResult[2] ? 'text--primary' : 'error--text'"
              >EMPRESA</span>
              <span
                class="text-xs"
                :class="stepValidationResult[2] ? 'text--secondary' : 'error--text'"
              >Endereço da empresa</span>
            </div>
          </div>
        </v-stepper-step>
      </v-stepper-header>

      <!-- Stepper Items -->
      <v-stepper-items>
        <!-- Stepper Content: Step 0 -->
        <v-stepper-content step="0">
          <v-form
            ref="step0Form"
            class="pt-2"
          >
            <stepper-cliente-settings-welcome
              :key="user.id"
              @handleStepValidation="handleStepValidation(0)"
            >
            </stepper-cliente-settings-welcome>
          </v-form>
        </v-stepper-content>

        <!-- Stepper Content: Step 1 -->
        <v-stepper-content step="1">
          <v-form
            ref="step1Form"
            class="pt-2"
          >
            <stepper-cliente-settings-account
              :key="user.id"
              :account-data="user"
              @refresh-data="getUser"
              @handleStepValidation="handleStepValidation(1)"
              @voltarhandleStepInfo="handleStepValidation(1, false)"
            >
            </stepper-cliente-settings-account>
          </v-form>
        </v-stepper-content>

        <!-- Stepper Content: Step 2 -->
        <v-stepper-content step="2">
          <v-form
            ref="step2Form"
            class="pb-6 pt-2"
          >
            <stepper-cliente-settings-info
              :key="user.id"
              :account-data="user"
              @handleStepInfo="handleStepValidation(2)"
              @handleStepInfos="redirecionar"
              @voltarhandleStepInfo="handleStepValidation(2, false)"
            >
            </stepper-cliente-settings-info>
          </v-form>
        </v-stepper-content>

        <!-- Stepper Content: Step 3 -->
        <v-stepper-content step="3">
          <v-form
            ref="step3Form"
            class="pb-6 pt-2"
          >
            <v-row class="mt-16 mb-16">
              <v-col cols="=12">
                <h1 class="text-center text-md-h3 text-xs-h4 font-weight-bold">
                  Seu cadastro foi concluído com sucesso! <br /><br />Você será redirecionado para nossa página de
                  produtos.
                </h1>
              </v-col>
            </v-row>
          </v-form>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-card>
</template>

<script>
import {
  mdiAccountOutline,
  mdiBellOutline,
  mdiBookmarkOutline,
  mdiInformationOutline,
  mdiLockOpenOutline,
} from '@mdi/js'
import { ref, onMounted } from '@vue/composition-api'
import { useRouter } from '@core/utils'
import {
  alphaDashValidator,
  alphaValidator,
  confirmedValidator,
  emailValidator,
  passwordValidator,
  required,
} from '@core/utils/validation'
import { storeToRefs } from 'pinia'
import StepperClienteSettingsWelcome from '@/views/empresa/stepper/StepperSettingsWelcome.vue'
import StepperClienteSettingsAccount from '@/views/empresa/stepper/StepperSettingsAccount.vue'
import StepperClienteSettingsInfo from '@/views/empresa/stepper/StepperSettingsInfo.vue'
import useUserStore from '@/store/user'
import ColaboradorService from '@/services/modules/ColaboradorService'

export default {
  components: {
    StepperClienteSettingsWelcome,
    StepperClienteSettingsAccount,
    StepperClienteSettingsInfo,
  },

  setup() {
    const activeStep = ref(0)
    const { router } = useRouter()

    const { id } = storeToRefs(useUserStore())

    const user = ref({})
    const getUser = async () => {
      user.value = await ColaboradorService.getById(id.value)
    }
    onMounted(getUser)

    const redirecionar = () => {
      setTimeout(() => {
        router.push({ name: 'conta' })
      }, 6000)
    }

    // Form Submitted Snackbar
    const isFormSubmittedSnackbarVisible = ref(false)

    // Template refs
    const step0Form = ref(null)
    const step1Form = ref(null)
    const step2Form = ref(null)
    const step3Form = ref(null)

    // Step Validation (for `rules` prop of `v-stepper-step`)
    // It will be updated when step is validated
    const stepValidationResult = ref({
      0: true,
      1: true,
      2: true,
      3: true,
    })

    // Step Validation
    const handleStepValidation = (step, navigateNext = true, isFinalStep = false) => {
      // Get Form templateRef of Step from argument
      const templateRef = (() => {
        if (step === 0) return step0Form
        if (step === 1) return step1Form
        if (step === 2) return step2Form
        if (step === 3) return step3Form

        return null
      })()

      // If no validate step templateRef is found just return
      if (!templateRef) return

      // If step is valid proceed to the next step
      const isStepValid = templateRef.value.validate()

      // Assign to stepValidationResult
      stepValidationResult.value[step] = isStepValid

      if (isStepValid) {
        if (navigateNext && !isFinalStep) activeStep.value += 1
        else if (!navigateNext) activeStep.value -= 1

        // Show Snackbar
        if (isFinalStep) {
          // ? Some Ajax request which saves form data in database
          isFormSubmittedSnackbarVisible.value = true
        }
      }
    }

    return {
      activeStep,
      user,
      getUser,
      redirecionar,

      // Field Validators
      validators: {
        required,
        alphaDashValidator,
        emailValidator,
        passwordValidator,
        confirmedValidator,
        alphaValidator,
      },

      // Step Validations
      stepValidationResult,
      handleStepValidation,

      // Template Refs
      step0Form,
      step1Form,
      step2Form,
      step3Form,

      // Snackbar
      isFormSubmittedSnackbarVisible,

      icons: {
        mdiAccountOutline,
        mdiLockOpenOutline,
        mdiInformationOutline,
        mdiBookmarkOutline,
        mdiBellOutline,
      },
    }
  },
}
</script>
